/** Clients: how the tenant picker groups operators (plan D4, D10).
 *
 * The unit of selection is the operator id. A *client* is presentation only: a
 * production operator with its sandbox folded in (`acme` + `acmesbx`), or an operator
 * that stands alone (a plain tenant, or a stand-alone / partner sandbox, which carries
 * the flask "Sandbox" mark). *Both* means the client's two ids.
 *
 * Pure functions — the picker, the AppBar scope chip and the grid empty state share
 * them, so a selection always reads the same: "Acme Adventures · Both", "+2".
 */
import type {
  TourOperatorEnvironment,
  TourOperatorListItem,
  TourOperatorPairRef,
} from '@/api/tourOperators';

/** What the picker needs to know about one operator to place it in its client. */
export interface TenantBrief {
  id: number;
  name: string;
  code: string;
  environment: TourOperatorEnvironment;
  /** A production operator's sandbox. */
  sandbox: TourOperatorPairRef | null;
  /** A sandbox's production operator; null for a stand-alone (partner) sandbox. */
  sandboxOf: TourOperatorPairRef | null;
}

/** The operator rows the list and detail endpoints return, as far as briefs go. */
export type BriefSource = Pick<TourOperatorListItem, 'id' | 'name' | 'code'> &
  Partial<Pick<TourOperatorListItem, 'environment' | 'sandbox' | 'sandbox_of' | 'sandbox_of_id'>>;

export function briefOf(row: BriefSource): TenantBrief {
  const sandboxOf = row.sandbox_of ?? null;
  const environment: TourOperatorEnvironment =
    row.environment ?? (sandboxOf || row.sandbox_of_id ? 'sandbox' : 'production');
  return {
    id: row.id,
    name: row.name,
    code: row.code,
    environment,
    sandbox: environment === 'production' ? (row.sandbox ?? null) : null,
    sandboxOf: environment === 'sandbox' ? sandboxOf : null,
  };
}

/** A row and, when it is a production operator with a sandbox, that sandbox too —
 *  a client row (`clients=1`) carries enough to place both. */
export function briefsOfRow(row: BriefSource): TenantBrief[] {
  const brief = briefOf(row);
  if (!brief.sandbox) return [brief];
  const { id, code, name } = brief.sandbox;
  return [
    brief,
    {
      id,
      code,
      name,
      environment: 'sandbox',
      sandbox: null,
      sandboxOf: { id: brief.id, code: brief.code, name: brief.name },
    },
  ];
}

/** One picker row's worth of operators. */
export interface TenantClient {
  /** The row's operator: the production operator, or the operator that stands alone. */
  id: number;
  name: string;
  code: string;
  /** The folded sandbox of a production operator. */
  sandbox: TourOperatorPairRef | null;
  /** A stand-alone (partner) sandbox: a row of its own with the flask mark. */
  standaloneSandbox: boolean;
}

export function clientOfBrief(b: TenantBrief): TenantClient {
  if (b.environment === 'sandbox' && b.sandboxOf) {
    return {
      id: b.sandboxOf.id,
      name: b.sandboxOf.name,
      code: b.sandboxOf.code,
      sandbox: { id: b.id, code: b.code, name: b.name },
      standaloneSandbox: false,
    };
  }
  return {
    id: b.id,
    name: b.name,
    code: b.code,
    sandbox: b.environment === 'production' ? b.sandbox : null,
    standaloneSandbox: b.environment === 'sandbox',
  };
}

/** Which of a client's operators are picked. A client without a sandbox is
 *  `production` when picked — or `sandbox` when it IS a stand-alone sandbox. */
export type ClientEnv = 'production' | 'sandbox' | 'both';
export type ClientState = ClientEnv | 'none';

export function clientIds(c: TenantClient): number[] {
  return c.sandbox ? [c.id, c.sandbox.id] : [c.id];
}

export function clientState(c: TenantClient, selected: ReadonlySet<number>): ClientState {
  if (c.sandbox) {
    const p = selected.has(c.id);
    const s = selected.has(c.sandbox.id);
    return p && s ? 'both' : p ? 'production' : s ? 'sandbox' : 'none';
  }
  if (!selected.has(c.id)) return 'none';
  return c.standaloneSandbox ? 'sandbox' : 'production';
}

/** The operator ids a client contributes in `env`. */
export function idsForEnv(c: TenantClient, env: ClientState): number[] {
  if (env === 'none') return [];
  if (!c.sandbox) return [c.id];
  if (env === 'both') return [c.id, c.sandbox.id];
  return env === 'sandbox' ? [c.sandbox.id] : [c.id];
}

const sorted = (ids: Iterable<number>) => [...new Set(ids)].sort((a, b) => a - b);

/** `selected` with exactly `env` of this client. */
export function setClientEnv(selected: readonly number[], c: TenantClient, env: ClientState): number[] {
  const mine = new Set(clientIds(c));
  return sorted([...selected.filter((id) => !mine.has(id)), ...idsForEnv(c, env)]);
}

/** A row click (name, code or checkbox): none or one → Both; Both → none. A client
 *  without a sandbox toggles its one operator. */
export function toggleClient(selected: readonly number[], c: TenantClient): number[] {
  const st = clientState(c, new Set(selected));
  if (!c.sandbox) return setClientEnv(selected, c, st === 'none' ? 'both' : 'none');
  return setClientEnv(selected, c, st === 'both' ? 'none' : 'both');
}

/** A segment click (or ⇧↵ / ⌥↵): exactly that set; the lit one again deselects. */
export function pickClientEnv(
  selected: readonly number[],
  c: TenantClient,
  env: ClientEnv,
): number[] {
  const st = clientState(c, new Set(selected));
  return setClientEnv(selected, c, st === env ? 'none' : env);
}

/** One picked client, for summaries. */
export interface ClientSelection {
  client: TenantClient;
  state: ClientEnv;
  /** No operator data (yet): the client is a bare `#id` or a name the caller gave. */
  unresolved?: boolean;
}

const ENV_WORD: Record<ClientEnv, string> = {
  production: 'Production',
  sandbox: 'Sandbox',
  both: 'Both',
};

/** The environment word a selection carries, or null. "Production" only where the
 *  client has a sandbox (every operator is production unless it says otherwise). */
export function envWord(sel: Pick<ClientSelection, 'client' | 'state'>): string | null {
  if (sel.state === 'both' || sel.state === 'sandbox') return ENV_WORD[sel.state];
  return sel.client.sandbox ? ENV_WORD.production : null;
}

/** The code of the operator(s) a selection names: the sandbox's for a sandbox pick. */
export function codeOf(sel: Pick<ClientSelection, 'client' | 'state'>): string {
  return sel.state === 'sandbox' && sel.client.sandbox ? sel.client.sandbox.code : sel.client.code;
}

/** "Acme Adventures · Both"; with `withCode`, "Acme Adventures (acme) · Both". */
export function describeSelection(sel: ClientSelection, { withCode = false } = {}): string {
  const code = withCode ? codeOf(sel) : '';
  const env = envWord(sel);
  return `${sel.client.name}${code ? ` (${code})` : ''}${env ? ` · ${env}` : ''}`;
}

/** The distinct clients of these operator ids, in the order of their first id.
 *
 *  `briefOfId` answers what is known about an operator; `nameOfId` the name a caller
 *  gave (a scope entry, a picker value). An id with no brief becomes a client of its
 *  own, named by the caller or `#id`, marked `unresolved`. */
export function clientsForIds(
  ids: readonly number[],
  briefOfId: (id: number) => TenantBrief | null | undefined,
  nameOfId: (id: number) => { name: string; code?: string } | null | undefined = () => null,
): { client: TenantClient; unresolved: boolean }[] {
  const out: { client: TenantClient; unresolved: boolean }[] = [];
  const seen = new Set<number>();
  for (const id of sorted(ids)) {
    const brief = briefOfId(id);
    const client: TenantClient = brief
      ? clientOfBrief(brief)
      : {
          id,
          name: nameOfId(id)?.name || `#${id}`,
          code: nameOfId(id)?.code ?? '',
          sandbox: null,
          standaloneSandbox: false,
        };
    if (seen.has(client.id)) continue;
    seen.add(client.id);
    out.push({ client, unresolved: !brief });
  }
  return out;
}

/** Group picked operator ids into clients (see `clientsForIds`), each with what it
 *  holds: *Production*, *Sandbox* or *Both*. */
export function groupSelection(
  ids: readonly number[],
  briefOfId: (id: number) => TenantBrief | null | undefined,
  nameOfId?: (id: number) => { name: string; code?: string } | null | undefined,
): ClientSelection[] {
  const selected = new Set(ids);
  const out: ClientSelection[] = [];
  for (const { client, unresolved } of clientsForIds(ids, briefOfId, nameOfId)) {
    const state = clientState(client, selected);
    if (state === 'none') continue;
    out.push(unresolved ? { client, state, unresolved } : { client, state });
  }
  return out;
}

/** The one-line summary of a selection: the first client, its environment word, and
 *  how many more there are; `full` lists them all (the tooltip). */
export interface SelectionSummary {
  first: ClientSelection;
  env: string | null;
  more: number;
  /** "Acme Adventures · Both, Bolt Lisbon · Production, Highland Rail". */
  full: string;
  /** The same with codes — accessible names and tooltips that must disambiguate. */
  fullWithCodes: string;
  /** "Acme Adventures · Both +2". */
  text: string;
}

export function summarizeSelection(sels: readonly ClientSelection[]): SelectionSummary | null {
  const first = sels[0];
  if (!first) return null;
  const env = envWord(first);
  const more = sels.length - 1;
  return {
    first,
    env,
    more,
    full: sels.map((s) => describeSelection(s)).join(', '),
    fullWithCodes: sels.map((s) => describeSelection(s, { withCode: true })).join(', '),
    text: `${describeSelection(first)}${more > 0 ? ` +${more}` : ''}`,
  };
}
